import { useEffect } from 'react'
import { ClipboardText, Lock, CheckCircle, XCircle } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { useAuditLogStore } from '@/stores/auditLogStore'
import { usePermission } from '@/hooks/usePermission'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'
import { describeAuditEntry } from '@/lib/auditLogFormat'
import { cardChromeClass, iconChipClass, iconChipAmberClass } from '@/lib/styleKit'

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  representative: 'Representative',
  rep: 'Representative', // legacy audit rows predating the value rename
  analyst: 'Analyst',
}

export default function AuditLogSection() {
  const { entries, loading, fetchEntries } = useAuditLogStore()
  const { allowed } = usePermission('audit_log', 'view')
  // The audit log shares the "Per-user credit budgets + audit log" pricing
  // row, so it shares that row's plan gate. Two independent doors, two
  // different messages: your ROLE doesn't include this vs your PLAN doesn't.
  const planLocked = usePlanFeatures().isLocked('per_user_credit_budgets')

  // Rep/Analyst have no PERMISSIONS["audit_log"]["view"] cell -- the
  // backend already 403s GET /audit-log/ for them, so don't even ask (an
  // empty-looking log is misleading; a plain "you don't have access to
  // this" is the honest, friendly message). Same reasoning for a plan that
  // does not include it: the route 403s, so don't ask.
  useEffect(() => {
    if (!allowed || planLocked) return
    fetchEntries().catch(() => {})
  }, [allowed, planLocked, fetchEntries])

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Audit log
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Who changed what on this account, and when.
        </p>
      </div>

      {!allowed ? (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>No access</CardTitle>
                <CardDescription>Your role doesn't include viewing the audit log</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : planLocked ? (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipAmberClass}>
                <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" weight="fill" />
              </div>
              <div>
                <CardTitle>Not included in your plan</CardTitle>
                <CardDescription>
                  The audit log and per-user credit budgets come with an upgrade — compare plans in
                  Billing.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <Card className={cardChromeClass}>
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <ClipboardText className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <div>
                <CardTitle>Activity</CardTitle>
                <CardDescription>Every access-sensitive change on this account, newest first</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {loading ? 'Loading…' : 'No audit log entries yet.'}
              </p>
            ) : (
              <div className="space-y-2">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] flex-wrap"
                  >
                    <div className="flex items-start gap-2.5 min-w-0">
                      {entry.result === 'denied' ? (
                        <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" weight="fill" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
                      )}
                      <div className="min-w-0">
                        <p
                          className="text-foreground dark:text-[#f7f8f8]"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', lineHeight: 1.45 }}
                        >
                          {describeAuditEntry(entry)}
                          {entry.result === 'denied' && (
                            <span className="text-destructive"> — denied</span>
                          )}
                        </p>
                        <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px', marginTop: '2px' }}>
                          {ROLE_LABELS[entry.actor_role] ?? entry.actor_role}
                        </p>
                      </div>
                    </div>
                    <span
                      className="text-muted-foreground dark:text-[#8a8f98] shrink-0"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
